import { useEffect } from 'react';
import {
  CHARACTER_ENGINE,
  MESSAGE_TYPES,
  RENDERER,
  SIMULATION_TYPES
} from '../constants/simulationConstants';
import {
  isCoreCharacterEngine,
  isNudgytAgentSimulationType
} from '../helpers/utils';
import { isMultiCharacterPayload } from '../helpers/multiCharacter';
import { useSimulationContext } from '../providers/SimulationProvider';
import useInitPolling from './useInitPolling';

function useInitMessageListener() {
  const {
    setRenderer,
    resetContext,
    setCharacterIds,
    setCharacterGlb,
    setEnvironmentId,
    setSimulationType,
    setCharacterEngineType,
    setConvaiSessionId,
    setAnamSessionToken,
    isClientInitialized,
    setNudgytSessionToken,
    setSelectedCharacterId,
    setPreviousLevelContext,
    setIsEmotionRecognitionEnabled,
    setIsMultiCharacter,
    setBotIds
  } = useSimulationContext();

  // Handle initialization polling (sends INITIALIZE messages to parent)
  useInitPolling();

  useEffect(() => {
    return () => {
      resetContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Simulation uses Nudgyt Agent client
   * Payload:
   *   characterIds (required)
   *   environmentId (required)
   *   simulationType (required) - COACHING | DESIGN | DELIVER, or BASIC when
   *     characterEngineType is NUDGYT_CORE
   *   characterEngineType (required for BASIC) - NUDGYT_CORE
   *   sessionToken (optional)
   */
  const handleNudgytAgentSimulation = (receivedDataPayload) => {
    // Claimed by handleMultiCharacterNudgytAgentSimulation below.
    if (receivedDataPayload?.isMultiCharacter) return;

    const isCoreCharacter = isCoreCharacterEngine(
      receivedDataPayload?.characterEngineType
    );

    if (
      !isNudgytAgentSimulationType(receivedDataPayload?.simulationType) &&
      !isCoreCharacter
    )
      return;

    setSimulationType(
      receivedDataPayload.simulationType ?? SIMULATION_TYPES.BASIC
    );
    setCharacterEngineType(receivedDataPayload.characterEngineType ?? null);
    setNudgytSessionToken(receivedDataPayload.nudgytAgentSessionToken ?? null);

    if (isCoreCharacter) {
      setIsEmotionRecognitionEnabled(
        receivedDataPayload?.isEmotionRecognitionEnabled ?? false
      );
    }
  };

  /**
   * Simulation uses Convai client
   * Payload:
   *   characterIds (required)
   *   environmentId (required)
   *   simulationType (required) — BASIC (or omitted)
   *   isEmotionRecognitionEnabled (optional)
   *   previousLevelContext (optional)
   *   convaiSessionId (optional)
   */
  const handleConvaiSimulation = (receivedDataPayload) => {
    // Claimed by handleMultiCharacterNudgytAgentSimulation below.
    if (receivedDataPayload?.isMultiCharacter) return;

    const isBasicSimulation =
      receivedDataPayload?.simulationType === SIMULATION_TYPES.BASIC ||
      !receivedDataPayload?.simulationType;

    if (!isBasicSimulation) return;
    // Claimed by handleNudgytAgentSimulation above: a Core character on a Basic
    // level converses through the agent, not Convai.
    if (isCoreCharacterEngine(receivedDataPayload?.characterEngineType)) return;

    setSimulationType(SIMULATION_TYPES.BASIC);
    setPreviousLevelContext(receivedDataPayload.previousLevelContext ?? null);
    setIsEmotionRecognitionEnabled(
      receivedDataPayload?.isEmotionRecognitionEnabled ?? false
    );
    setConvaiSessionId(receivedDataPayload.convaiSessionId ?? null);
  };

  /**
   * Multi-character Basic simulation: several Nudgyt Core characters on 3JS
   * avatars sharing one Nudgyt Agent session.
   * Payload:
   *   isMultiCharacter (required) - true
   *   characterIds (required) - one avatar mesh id per character
   *   botIds (required) - one Core bot id per character, same order
   *   environmentId (required)
   *   nudgytAgentSessionToken (required)
   *   isEmotionRecognitionEnabled (optional)
   *
   * Engine and simulation type are fixed to Core + BASIC, which the chat, Live
   * Chat and user-emotion listeners already gate on.
   */
  const handleMultiCharacterNudgytAgentSimulation = (receivedDataPayload) => {
    if (!receivedDataPayload?.isMultiCharacter) {
      // A second INITIALIZE for a single-character level has to clear the flag.
      // resetContext only runs on unmount, and SimulationContainer checks
      // isMultiCharacter first, so a stale `true` keeps mounting the
      // multi-character client against a session that has one bot.
      setIsMultiCharacter(false);
      setBotIds([]);
      return;
    }

    if (!isMultiCharacterPayload(receivedDataPayload)) {
      console.warn(
        '[Init Message Listener] Multi-character payload needs one bot id per character id'
      );
      return;
    }

    setIsMultiCharacter(true);
    setBotIds(receivedDataPayload.botIds);
    setSimulationType(SIMULATION_TYPES.BASIC);
    setCharacterEngineType(CHARACTER_ENGINE.NUDGYT_CORE);
    setNudgytSessionToken(receivedDataPayload.nudgytAgentSessionToken ?? null);
    setIsEmotionRecognitionEnabled(
      receivedDataPayload?.isEmotionRecognitionEnabled ?? false
    );
  };

  useEffect(() => {
    // Handles incoming postMessage events from parent application
    const handleMessageEvent = (event) => {
      try {
        let receivedData = event.data;

        // Parse JSON if the received data is a string
        if (typeof receivedData === 'string') {
          try {
            receivedData = JSON.parse(receivedData);
          } catch (err) {
            return;
          }
        }

        const receivedDataPayload = receivedData.payload;

        // validate payload structure
        if (
          receivedData.type !== MESSAGE_TYPES.INITIALIZE ||
          typeof receivedDataPayload.characterIds !== 'object' ||
          typeof receivedDataPayload.environmentId !== 'string'
        )
          return;

        const characterIds = receivedDataPayload.characterIds;

        if (!characterIds?.length) return;
        console.debug(
          '[DEBUG] Received useInitMessageListener message',
          receivedData
        );
        setCharacterIds(characterIds);
        setCharacterGlb(receivedDataPayload.characterGlb ?? null);
        setSelectedCharacterId(characterIds[0]);
        setEnvironmentId(receivedDataPayload.environmentId);
        setRenderer(receivedDataPayload.renderer ?? RENDERER.THREE_JS);
        setAnamSessionToken(receivedDataPayload?.anamSessionToken ?? null);

        handleMultiCharacterNudgytAgentSimulation(receivedDataPayload);
        handleNudgytAgentSimulation(receivedDataPayload);
        handleConvaiSimulation(receivedDataPayload);
      } catch (error) {
        console.error('[Init Message Listener Error]', error);
      }
    };

    // Add event listener to handle message
    window.addEventListener('message', handleMessageEvent);
    return () => window.removeEventListener('message', handleMessageEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientInitialized, setCharacterIds, setEnvironmentId]);
}

export default useInitMessageListener;
